/**
 * Route protegee pour super_admin uniquement
 * Gere l'auth ET la verification super_admin (independant de ProtectedRoute)
 * Utilise la fonction RPC is_super_admin (SECURITY DEFINER) pour bypass RLS
 * Fallback: verification directe en base si le RPC echoue
 */

import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useState, useEffect, useRef } from 'react';

interface SuperAdminRouteProps {
  children: React.ReactNode;
}

export function SuperAdminRoute({ children }: SuperAdminRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const hasShownToast = useRef(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user?.id) {
      setIsSuperAdmin(null);
      return;
    }

    let cancelled = false;

    const checkSuperAdmin = async () => {
      try {
        // Methode 1: RPC is_super_admin (SECURITY DEFINER, bypass RLS)
        const { data, error } = await supabase.rpc('is_super_admin');

        if (!cancelled) {
          if (error) {
            console.error('[SuperAdminRoute] RPC error, trying direct query:', error);
            // Fallback: requete directe sur user_roles
            const { data: roles } = await supabase
              .from('user_roles')
              .select('role')
              .eq('user_id', user.id)
              .eq('role', 'super_admin')
              .maybeSingle();

            if (!cancelled) {
              setIsSuperAdmin(!!roles);
            }
          } else {
            setIsSuperAdmin(data === true);
          }
        }
      } catch (err) {
        console.error('[SuperAdminRoute] Exception:', err);
        if (!cancelled) setIsSuperAdmin(false);
      }
    };

    checkSuperAdmin();
    return () => { cancelled = true; };
  }, [user?.id]);

  // Auth en cours de chargement
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          <p className="text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  // Pas connecte
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Verification du role en cours
  if (isSuperAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
          <p className="text-muted-foreground">Verification des droits...</p>
        </div>
      </div>
    );
  }

  // Connecte mais pas super_admin
  if (!isSuperAdmin) {
    if (!hasShownToast.current) {
      hasShownToast.current = true;
      toast({
        title: 'Acces refuse',
        description: 'Cette section est reservee aux administrateurs de la plateforme.',
        variant: 'destructive',
      });
    }
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
